// Видимость элементов меню и счётчиков. Принцип «по умолчанию пусто»:
// приложение стартует чистым, пользователь сам включает нужное в
// Настройках → «Меню приложения». Все флаги по умолчанию ВЫКЛ.
//
// Хранится в users.menu_visibility (jsonb). Пустой объект / отсутствующий
// ключ = скрыто (см. parseMenuVisibility). Это значит, что существующие
// пользователи после деплоя получают чистый старт без миграции данных.

export type MenuVisibility = {
  /** Плитка-счётчик «разобрать» (число неразобранных). */
  countUnresolved: boolean;
  /** Плитка-счётчик «закреплено». */
  countPinned: boolean;
  /** Плитка-счётчик «в списках» (Лучшее + Сделано + Корзина). */
  countLists: boolean;
  /** Фильтр по чатам-источникам («Все чаты») над лентой. Сам компонент к тому
   *  же скрывается, если записи приходят меньше чем из двух чатов. */
  chatSelector: boolean;
  /** Раздел «Закреп» в нав-баре. */
  sectionPinned: boolean;
  /** Раздел «Мои списки» в нав-баре. */
  sectionLists: boolean;
};

export const MENU_VISIBILITY_KEYS: readonly (keyof MenuVisibility)[] = [
  'countUnresolved',
  'countPinned',
  'countLists',
  'chatSelector',
  'sectionPinned',
  'sectionLists',
];

/** Дефолт: всё скрыто. */
export const DEFAULT_MENU_VISIBILITY: MenuVisibility = {
  countUnresolved: false,
  countPinned: false,
  countLists: false,
  chatSelector: false,
  sectionPinned: false,
  sectionLists: false,
};

/**
 * Нормализует сырое значение из БД (jsonb / null / частичный или «грязный»
 * объект) в полный объект MenuVisibility. Любой отсутствующий, не-boolean или
 * неизвестный ключ → false (скрыто). Строгая проверка `=== true` — чтобы
 * случайные truthy-значения ('false', 1, 'on') не включали пункт по ошибке.
 */
export function parseMenuVisibility(raw: unknown): MenuVisibility {
  const src =
    raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const result = { ...DEFAULT_MENU_VISIBILITY };
  for (const key of MENU_VISIBILITY_KEYS) {
    result[key] = src[key] === true;
  }
  return result;
}

/** Нужно ли рисовать блок плиток-счётчиков (есть хоть один видимый). */
export function hasVisibleCounters(mv: MenuVisibility): boolean {
  return mv.countUnresolved || mv.countPinned || mv.countLists;
}

/**
 * Видим ли мобильный нижний нав-бар. «Лента» присутствует всегда, но бар из
 * одной «Ленты» бессмыслен — показываем только если включён хотя бы один из
 * разделов (Закреп / Мои списки). Когда оба выключены, бар прячется целиком,
 * а кнопка добавления опускается на освободившееся место.
 */
export function isMobileNavVisible(mv: MenuVisibility): boolean {
  return mv.sectionPinned || mv.sectionLists;
}
